// Lieferanten und Partien (E3c, S1) – was die Seite rechnet, ohne React: die
// drei Abschnitte mit ihrem Satz, der Entwurf beim Anlegen und Ändern, und
// was daraus an den Server geht. Die Regeln selbst stehen im Kern
// (ablauf-vertrag.ts, partnerEingabe) – hier wird nur aus den Feldern die
// Eingabe, mit einer Meldung am Feld, wenn etwas fehlt.

import { ABLAUF_GRENZEN, PARTNER_ARTEN, partnerEingabe, type PartnerArt, type PartnerEingabe, type PartnerZeile } from "@happy-energy/kern";
import type { IkonName } from "@happy-energy/oberflaeche";

/**
 * Je Art, was die Seite dazu sagt – selbsterklärend (G19): wofür es ist und
 * wo es später gebraucht wird. „knopf“: was „+ …“ anlegt.
 */
export const ABSCHNITTE: Record<PartnerArt, { titel: string; satz: string; ikon: IkonName; knopf: string; neu: string; beispiel: string; mitMail: boolean }> = {
  lieferant: {
    titel: "Lieferanten",
    satz: "Bei ihnen bestellt ihr Unterkonstruktion, Module, Wechselrichter und Speicher. Bei jeder Anlage tippst du später nur noch an, wer liefert – die Mail an den Ansprechpartner ist dann schon fertig.",
    ikon: "paket",
    knopf: "Lieferant",
    neu: "Neuer Lieferant",
    beispiel: "z. B. Gautzsch",
    mitMail: true,
  },
  dachpartie: {
    titel: "Dachpartien",
    satz: "Die Firmen, die die Module aufs Dach bringen. Bei jeder Anlage tippst du später nur noch an, welche kommt – sie bekommt Termin, Adresse und die Unterlage fürs Dach per Mail.",
    ikon: "dach",
    knopf: "Dachpartie",
    neu: "Neue Dachpartie",
    beispiel: "z. B. Dachbau Huber",
    mitMail: true,
  },
  bodenpartie: {
    titel: "Bodenpartien",
    satz: "Eure eigenen Leute für Wechselrichter, Speicher und Verkabelung. Bei jeder Anlage tippst du später nur noch an, welche Partie kommt – die Unterlage druckst du ihr aus, bis die Partien eine eigene App haben.",
    ikon: "werkzeug",
    knopf: "Bodenpartie",
    neu: "Neue Bodenpartie",
    beispiel: "z. B. Partie Josef",
    mitMail: false,
  },
};

/** Ein Ansprechpartner, wie er im Formular steht – alles Text, leer heißt „nicht angegeben“. */
export interface PersonEntwurf {
  name: string;
  email: string;
  telefon: string;
}

/** Ein Lieferant oder eine Partie im Formular. `neu`: noch nicht am Server. */
export interface PartnerEntwurf {
  id: string;
  art: PartnerArt;
  name: string;
  ansprechpartner: PersonEntwurf[];
  aktiv: boolean;
  neu: boolean;
}

const LEERE_PERSON: PersonEntwurf = { name: "", email: "", telefon: "" };

/** Ein neuer Eintrag – mit einer leeren Zeile für den ersten Ansprechpartner. */
export function neuerEntwurf(id: string, art: PartnerArt): PartnerEntwurf {
  return { id, art, name: "", ansprechpartner: [{ ...LEERE_PERSON }], aktiv: true, neu: true };
}

/** Ein Eintrag vom Server zum Ändern – ohne Ansprechpartner mit einer leeren Zeile, damit man gleich einen eintragen kann. */
export function entwurfAus(p: PartnerZeile): PartnerEntwurf {
  const leute = p.ansprechpartner.map((a) => ({ name: a.name, email: a.email ?? "", telefon: a.telefon ?? "" }));
  return { id: p.id, art: p.art, name: p.name, ansprechpartner: leute.length > 0 ? leute : [{ ...LEERE_PERSON }], aktiv: p.aktiv, neu: false };
}

/** Darf noch ein Ansprechpartner dazu? */
export function nochEinePerson(e: PartnerEntwurf): boolean {
  return e.ansprechpartner.length < ABLAUF_GRENZEN.ansprechpartner;
}

const ganzLeer = (p: PersonEntwurf) => !p.name.trim() && !p.email.trim() && !p.telefon.trim();

/** Wo die Meldung hingehört: „name“ oder die Zeile eines Ansprechpartners samt Feld. */
export type FeldOrt = { feld: "name" } | { feld: "person"; nr: number; teil: keyof PersonEntwurf } | { feld: null };

export type Pruefung = { ok: true; eingabe: PartnerEingabe } | { ok: false; ort: FeldOrt; meldung: string };

/**
 * Aus dem Formular die Eingabe für den Server. Ganz leere Zeilen für
 * Ansprechpartner fallen weg (so entfernt man einen: alles leeren oder
 * „entfernen“). Die Nummer in der Meldung zählt die Zeilen, wie sie
 * dastehen.
 */
export function eingabeAus(e: PartnerEntwurf): Pruefung {
  const behalten = e.ansprechpartner.map((p, nr) => ({ p, nr })).filter(({ p }) => !ganzLeer(p));
  const gelesen = partnerEingabe.safeParse({
    id: e.id,
    art: e.art,
    name: e.name,
    ansprechpartner: behalten.map(({ p }) => ({ name: p.name, email: p.email, telefon: p.telefon })),
    aktiv: e.aktiv,
  });
  if (gelesen.success) return { ok: true, eingabe: gelesen.data };
  const erste = gelesen.error.issues[0];
  const meldung = erste?.message ?? "Bitte die Felder prüfen.";
  const [wo, stelle, teil] = erste?.path ?? [];
  if (wo === "name") return { ok: false, ort: { feld: "name" }, meldung };
  if (wo === "ansprechpartner" && typeof stelle === "number" && (teil === "name" || teil === "email" || teil === "telefon")) {
    return { ok: false, ort: { feld: "person", nr: behalten[stelle]?.nr ?? 0, teil }, meldung };
  }
  return { ok: false, ort: { feld: null }, meldung };
}

/** Die Einträge einer Art: die angebotenen und die „nicht mehr angebotenen“ – in der Reihenfolge des Servers. */
export function nachArt(liste: readonly PartnerZeile[]): Record<PartnerArt, { aktiv: PartnerZeile[]; inaktiv: PartnerZeile[] }> {
  const ergebnis = Object.fromEntries(PARTNER_ARTEN.map((art) => [art, { aktiv: [] as PartnerZeile[], inaktiv: [] as PartnerZeile[] }])) as Record<
    PartnerArt,
    { aktiv: PartnerZeile[]; inaktiv: PartnerZeile[] }
  >;
  for (const p of liste) ergebnis[p.art][p.aktiv ? "aktiv" : "inaktiv"].push(p);
  return ergebnis;
}

/** Ein Ansprechpartner in einer Zeile: „Anes · anes@gautzsch.at · 0316 123456“ – was fehlt, fällt weg. */
export function personText(p: { name: string; email: string | null; telefon: string | null }): string {
  return [p.name, p.email, p.telefon].filter((t): t is string => Boolean(t)).join(" · ");
}

/**
 * Was am Eintrag noch fehlt, damit die Mail später fertig werden kann – bei
 * Lieferanten und Dachpartien eine Mailadresse. Null: nichts.
 */
export function hinweisFuer(p: Pick<PartnerZeile, "art" | "ansprechpartner">): string | null {
  if (!ABSCHNITTE[p.art].mitMail) return null;
  if (p.ansprechpartner.some((a) => a.email)) return null;
  return "Noch keine Mailadresse – ohne sie kann das CRM die Mail nicht fertig machen.";
}
